import { useCallback } from 'react';

interface UseMessageModalActionsInput {
  loading: boolean;
  primaryLoading: boolean;
  closeOnPrimary: boolean;
  closeOnSecondary: boolean;
  onClose: () => void;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
}

interface MessageModalActionInput {
  loading: boolean;
  shouldClose: boolean;
  onClose: () => void;
  onAction?: () => void;
}

interface MessageModalActions {
  handlePrimary: () => void;
  handleSecondary: () => void;
}

enum MessageModalActionMode {
  CloseFirst = 'close-first',
  Immediate = 'immediate',
}

const MESSAGE_MODAL_ACTION_RUNNER_BY_MODE: Record<
  MessageModalActionMode,
  (onAction?: () => void) => void
> = {
  [MessageModalActionMode.CloseFirst]: (onAction): void => {
    if (onAction) {
      setTimeout(onAction, 0);
    }
  },
  [MessageModalActionMode.Immediate]: (onAction): void => {
    if (onAction) {
      onAction();
    }
  },
};

function runMessageModalAction({
  loading,
  shouldClose,
  onClose,
  onAction,
}: MessageModalActionInput): void {
  if (loading) {
    return;
  }

  const actionMode = shouldClose
    ? MessageModalActionMode.CloseFirst
    : MessageModalActionMode.Immediate;

  if (shouldClose) {
    onClose();
  }

  MESSAGE_MODAL_ACTION_RUNNER_BY_MODE[actionMode](onAction);
}

export function useMessageModalActions({
  loading,
  primaryLoading,
  closeOnPrimary,
  closeOnSecondary,
  onClose,
  onPrimaryClick,
  onSecondaryClick,
}: UseMessageModalActionsInput): MessageModalActions {
  const handlePrimary = useCallback((): void => {
    runMessageModalAction({
      loading: loading || primaryLoading,
      shouldClose: closeOnPrimary,
      onClose,
      onAction: onPrimaryClick,
    });
  }, [closeOnPrimary, loading, onClose, onPrimaryClick, primaryLoading]);

  const handleSecondary = useCallback((): void => {
    runMessageModalAction({
      loading,
      shouldClose: closeOnSecondary,
      onClose,
      onAction: onSecondaryClick,
    });
  }, [closeOnSecondary, loading, onClose, onSecondaryClick]);

  return {
    handlePrimary,
    handleSecondary,
  };
}
